<script setup lang="ts">
import DbButton from "@/components/DB-Components/DbButton.vue";
import DbInput from "@/components/DB-Components/DbInput.vue";
import { UserData } from "@/data/interfaces/UserDataInterface";
import { reAuthorize } from "@/services/auth/re-auth";
import store from "@/store";
import router from "@/router";
import { onMounted, ref } from "vue";
import {
  isFieldRequired,
  isFieldValid,
  validationMessage,
} from "@/utils/users/methods";

const user = ref({
  username: "",
  first_name: "",
  last_name: "",
  phone_number: "",
});

const form = ref({
  errors: {},
  processing: false,
});

onMounted(() => {
  reAuthorize(store).then((res) => {
    store
      .dispatch("UserStore/getUser", store.state.user.data.user_id)
      .then((response) => {
        user.value = store.state.UserStore.user as UserData;
      });
  });
});

const update = async () => {
  form.value.processing = true;
  if (!isFormValid()) {
    return;
  }
  await store
    .dispatch("UserStore/editUser", user.value)
    .then((res) => {
      router.push("/");
    })
    .catch((error) => {
      const data = error?.response?.data;
      const errorStatus = data?.httpCode;
      if (errorStatus == 403) {
        form.value.errors = data.httpMessage;
      }
    });
};

const isFormValid = () => {
  let isFilled = true;
  // Check validity of all fields
  const allFieldsValid = Object.keys(user.value).every((fieldName) =>
    isFieldValid(fieldName, user.value)
  );
  if (!allFieldsValid) return false;

  if (form.value.processing == false) {
    isFilled =
      Object.keys(user.value).filter(
        (fieldName) =>
          isFieldRequired(fieldName) &&
          (user.value[fieldName] == null || user.value[fieldName]?.length == 0)
      ).length > 0
        ? false
        : true;
    return isFilled;
  }
  return true;
};
</script>
<template>
  <div id="profile">
    <div class="flex flex-col p-2 mx-2">
      <div class="w-full flex flex-col mb-3 items-center">
        <div class="flex w-full flex-col">
          <p class="self-end text-sm">{{ $t("account.required") }}</p>
          <p class="self-start text-sm">
            {{ $t("account.username_dispatcher_message") }}
          </p>
        </div>
        <DbInput
          :disabled="true"
          v-model="user.username"
          :value="user.username"
          :placeholder="$t('account.username')"
          :label="$t('account.username')"
          class="w-full hydrated"
        ></DbInput>
        <span
          v-if="!isFieldValid('username', user) && form.processing"
          class="text-red-500"
        >
          {{ $t("account.validation.username") }}
        </span>
      </div>
      <hr class="mb-3" />
      <div
        class="w-full flex flex-col items-center"
        :class="{
          'pb-6': !form.processing || isFieldValid('first_name', user),
          'pb-0': !isFieldValid('first_name', user) && form.processing,
        }"
      >
        <DbInput
          v-model="user.first_name"
          :value="user.first_name"
          :class="{
            'border-red-500':
              !isFieldValid('first_name', user) && form.processing,
          }"
          :placeholder="$t('account.first_name')"
          :label="$t('account.first_name')"
          :ariainvalid="
            !isFieldValid('first_name', user) && form.processing ? 'true' : ''
          "
          class="w-full hydrated"
        ></DbInput>
        <span
          v-if="!isFieldValid('first_name', user) && form.processing"
          class="text-red-500"
        >
          {{ $t("account.validation.first_name") }}
        </span>
      </div>
      <div
        class="w-full flex flex-col items-center"
        :class="{
          'pb-6': !form.processing || isFieldValid('last_name', user),
          'pb-0': !isFieldValid('last_name', user) && form.processing,
        }"
      >
        <DbInput
          v-model="user.last_name"
          :value="user.last_name"
          :class="{
            'border-red-500':
              !isFieldValid('last_name', user) && form.processing,
          }"
          :placeholder="$t('account.last_name')"
          :label="$t('account.last_name')"
          :ariainvalid="
            !isFieldValid('last_name', user) && form.processing ? 'true' : ''
          "
          class="w-full hydrated"
        ></DbInput>
        <span
          v-if="!isFieldValid('last_name', user) && form.processing"
          class="text-red-500"
        >
          {{ $t("account.validation.last_name") }}
        </span>
      </div>

      <div
        class="w-full flex flex-col items-center"
        :class="{
          'pb-6': !form.processing || isFieldValid('phone_number', user),
          'pb-0': !isFieldValid('phone_number', user) && form.processing,
        }"
      >
        <div class="flex w-full flex-col">
          <p class="self-start text-sm">
            {{ $t("account.phone_number_required_to_reset_password") }}
          </p>
        </div>
        <DbInput
          v-model="user.phone_number"
          :value="user.phone_number"
          :class="{
            'border-red-500':
              !isFieldValid('phone_number', user) && form.processing,
          }"
          :placeholder="$t('account.phone_number')"
          :label="$t('account.phone_number')"
          :ariainvalid="
            !isFieldValid('phone_number', user) && form.processing ? 'true' : ''
          "
          class="w-full hydrated"
        ></DbInput>
        <span
          v-if="!isFieldValid('phone_number', user) && form.processing"
          class="text-red-500"
        >
          {{ $t("account.validation.phone_number") }}
        </span>
      </div>

      <div class="flex flex-col gap-3">
        <div class="w-full flex items-center justify-center">
          <DbButton
            class="w-full"
            @click="() => router.push('/reset-password')"
            >{{ $t("account.password_reset") }}</DbButton
          >
        </div>
        <div class="w-full flex items-center justify-center">
          <DbButton
            class="w-full"
            @click="() => router.push('/')"
            variant="secondary-solid"
            >{{ $t("account.cancel") }}</DbButton
          >
        </div>
        <div class="w-full flex items-center justify-center">
          <DbButton @click="update" variant="primary">{{
            $t("account.save")
          }}</DbButton>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
#profile
  .elm-input.sc-db-input:not(:placeholder-shown):not(
    :has(+ label[data-label-hidden="true"])
  ).sc-db-input {
  padding-top: 1rem !important;
}
</style>
